import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import app from "nystem";
import { DatabaseSearchContext, ContentTypeRender, Wrapper, Image, DateView, Icon } from "nystem-components";
import { createPortal } from "react-dom";
import { photoCity } from "../../client/photoCity.js";
import { photoTimeline } from "../../client/photoTimeline.js";
import { PhotoTimelineContext } from "../../client/photoTimelineContext.js";
import { photoRegion, matchesPhotoRegion } from "../../client/photoRegion.js";

const photoHeading = (image) => {
  const region = photoRegion(image);
  return image.inferredLocation || [...new Set([photoCity(image), region.region, region.country].filter(Boolean))].join(", ") || "Between";
};

const PhotographTimelineImage = ({ image, openImage, imageTags, tag, toggleTag }) => {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <figure id={`trip-photo-${image._id}`} tabIndex={-1} className="relative min-w-0 scroll-mt-[calc(40vh+9rem)] lg:scroll-mt-[calc(min(50vh,640px)+7rem)]">
    <button type="button" aria-label={`Open photo ${image.title || image.file?.name || image._id}`} onClick={() => openImage(image)} className="block w-full max-sm:-mx-6 max-sm:w-[calc(100%+3rem)]">
      <div ref={ref} className="relative max-h-[600px] w-full overflow-hidden bg-stone-100" style={{ aspectRatio: `${image.file?.width || 4} / ${image.file?.height || 3}` }}>
        {visible && image.file && <Image src={`/${image.file.type}/${image.file.width || 1200}x${image.file.height || 1200}/${image.file.id}.${image.file.ext}`} alt={image.alt || image.title || image.file.name} className="absolute inset-0 h-full w-full object-contain object-top" />}
      </div>
    </button>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <figcaption className="mt-2 text-sm text-stone-600 max-sm:-mx-3">
      <div className="flex items-baseline gap-2">
        <time className="shrink-0 font-bold" dateTime={new Date(image._crdate).toISOString()}><DateView model={{ renderAs: "span", dateFormat: "HH:mm" }} value={image._crdate} /></time>
        {!image.inferredLocation && image.place && <span>{image.place.split(",").map((part) => part.trim().replace(/^(?:\d{2}-\d{3}|\d{3}\s?\d{2}|\d{4}\s?[A-Z]{2}|\d{4,5}|[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s+/i, "").replace(/^\d+[a-z]?(?:[-–/]\d+[a-z]?)?\s+/i, "").replace(/(?:^|\s)\d+[a-z]?(?:[-–/]\d+[a-z]?)?$/i, "").trim()).filter((part) => part && !photoHeading(image).split(",").some((heading) => heading.trim().toLocaleLowerCase() === part.toLocaleLowerCase())).join(", ")}</span>}
        <button type="button" aria-label="Image tags" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className={`ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-stone-200 ${imageTags.some((imageTag) => tag.includes(imageTag._id)) ? "text-[#233d35]" : "text-stone-500"}`}>
          <Icon icon="tags" className="h-4 w-4" />
        </button>
      </div>
      {expanded && <div className="mt-2 flex flex-wrap gap-2" aria-label="Tags in Image category">
        {imageTags.map((imageTag) => <button key={imageTag._id} type="button" aria-pressed={tag.includes(imageTag._id)} disabled={saving} onClick={async () => {
          setSaving(true);
          setError("");
          try { await toggleTag(image, imageTag); }
          catch { setError("Tag could not be saved."); }
          finally { setSaving(false); }
        }} className={`flex items-center gap-2 rounded border px-3 py-2 disabled:opacity-50 ${tag.includes(imageTag._id) ? "border-[#233d35] bg-[#e5ebe2] text-[#233d35]" : "border-stone-300"}`}>
          {imageTag.icon && <Icon icon={imageTag.icon} className={`h-4 w-4 ${imageTag.icon === "heart" && tag.includes(imageTag._id) ? "text-red-600" : ""}`} />}
          {imageTag.name}
        </button>)}
        {!imageTags.length && <span>No Image tags available.</span>}
      </div>}
      {image.camera && <p>{image.camera}</p>}
      {image.caption && <p>{image.caption}</p>}
    </figcaption>
  </figure>;
};

const PhotographRegions = ({ model, path }) => {
  const context = useContext(DatabaseSearchContext);
  const sentinelRef = useRef();
  const timelineRef = useRef();
  const stickyRef = useRef();
  const stickyStartRef = useRef();
  const [hideFilters, setHideFilters] = useState(false);
  const [activeImage, setActiveImage] = useState();
  const [imageTags, setImageTags] = useState([]);
  const [tags, setTags] = useState({});
  useEffect(() => {
    if (model.trips) return;
    app.database.tag.search({ filter: { name: "Image" }, count: 1 }).then(async ({ data }) => {
      if (!data?.length) return;
      const result = await app.database.tag.search({ filter: { group: data[0]._id }, count: 1000 });
      setImageTags(result.data || []);
    });
  }, [model.trips]);
  const toggleTag = async (image, imageTag) => {
    const { data, error } = await app.database.image.get({ id: image._id });
    if (error) throw new Error(error);
    const tag = (data.tag || []).includes(imageTag._id) ? data.tag.filter((tag) => tag !== imageTag._id) : [...(data.tag || []), imageTag._id];
    const saved = await app.database.image.save({ fields: true, data: { _id: image._id, tag } });
    if (saved.error) throw new Error(saved.error);
    setTags((tags) => ({ ...tags, [image._id]: tag }));
  };
  const dialogRef = useRef();
  const [selected, setSelected] = useState();
  const [image, openImage] = useState();
  const [count, setCount] = useState(24);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!model.trips) return undefined;
    let mounted = true;
    app.database.image.search({ count: 100000 }).then((result) => {
      if (!mounted) return;
      if (result.error) setError("Photo locations could not be loaded.");
      else setImages(result.data || []);
    }).catch(() => { if (mounted) setError("Photo locations could not be loaded."); });
    return () => { mounted = false; };
  }, [model.trips]);
  const data = useMemo(() => model.trips ? context.search?.data || [] : photoTimeline(context.search?.data || []), [context.search?.data, model.trips]);
  const tripImages = useMemo(() => model.trips ? new Map(data.map((trip) => [trip._id, photoTimeline(images.filter((image) => image._crdate >= trip.startTime && image._crdate <= trip.endTime))])) : undefined, [model.trips, data, images]);
  const regions = model.trips ? [...tripImages.values()].flat() : data;
  const countries = [...new Set(regions.map((image) => photoRegion(image).country).filter(Boolean))].sort();
  const availableRegions = [...new Set(regions.filter((image) => matchesPhotoRegion(image, country, "")).map((image) => photoRegion(image).region).filter(Boolean))].sort();
  const filtered = useMemo(() => !country && !region ? data : data.filter((item) => model.trips ? tripImages.get(item._id).some((image) => matchesPhotoRegion(image, country, region)) : matchesPhotoRegion(item, country, region)), [data, tripImages, country, region, model.trips]);
  useEffect(() => { setCount(24); setSelected(undefined); }, [country, region]);
  useEffect(() => {
    if (!selected) return;
    const element = document.getElementById(`trip-photo-${selected._id}`);
    if (!element) return;
    element.scrollIntoView({ block: "start" });
    setSelected(undefined);
  }, [selected, count]);
  useEffect(() => {
    if (!image) return undefined;
    const focused = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current.showModal();
    return () => { document.body.style.overflow = overflow; focused?.focus({ preventScroll: true }); };
  }, [image]);
  const selectImage = (image) => {
    setActiveImage(image);
    const index = filtered.findIndex((item) => item._id === image._id);
    setCount((count) => Math.max(count, Math.ceil((index + 1) / 24) * 24));
    setSelected({ _id: image._id });
  };
  useEffect(() => {
    if (model.trips || count >= filtered.length || !sentinelRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setCount((count) => count + 24); });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [count, filtered.length, model.trips]);
  useEffect(() => {
    if (model.trips) return undefined;
    let frame;
    const scroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setHideFilters(stickyStartRef.current.getBoundingClientRect().top < 0);
        const bottom = stickyRef.current.getBoundingClientRect().bottom;
        const element = [...timelineRef.current.querySelectorAll("figure")].findLast((element) => element.getBoundingClientRect().top <= bottom + 16) || timelineRef.current.querySelector("figure");
        if (element) setActiveImage(filtered.find((image) => `trip-photo-${image._id}` === element.id));
      });
    };
    window.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("resize", scroll);
    scroll();
    return () => { cancelAnimationFrame(frame); window.removeEventListener("scroll", scroll); window.removeEventListener("resize", scroll); };
  }, [filtered, count, model.trips, hideFilters]);
  const content = <PhotoTimelineContext.Provider value={{ selectImage, activeImage }}><DatabaseSearchContext.Provider value={{ ...context, search: { ...context.search, data: filtered } }}><ContentTypeRender path={path} items={model.item} /></DatabaseSearchContext.Provider></PhotoTimelineContext.Provider>;
  return <Wrapper className={model.trips ? "" : "trip-timeline [overflow-anchor:none]"}>
    <div ref={stickyStartRef} />
    <div ref={stickyRef} className={model.trips ? "" : "sticky top-0 z-20 -mx-6 bg-[#f6f5ef] px-6"}>
    {!model.trips && content}
    <div className={`${hideFilters ? "hidden" : "flex"} flex-wrap items-center gap-x-4 gap-y-2 py-2 max-sm:flex-nowrap max-sm:gap-x-2`}>
      <label className="flex items-center gap-2 text-sm max-sm:min-w-0 max-sm:flex-1"><span className="sr-only sm:not-sr-only">Country</span><select className="min-w-36 border-stone-300 bg-transparent py-1 text-sm max-sm:w-full max-sm:min-w-0 max-sm:pl-2 max-sm:pr-6 max-sm:text-xs" value={country} onChange={(event) => { setCountry(event.target.value); setRegion(""); }}><option value="">All countries</option>{countries.map((country) => <option key={country}>{country}</option>)}</select></label>
      <label className="flex items-center gap-2 text-sm max-sm:min-w-0 max-sm:flex-1"><span className="sr-only sm:not-sr-only">Region</span><select className="min-w-36 border-stone-300 bg-transparent py-1 text-sm max-sm:w-full max-sm:min-w-0 max-sm:pl-2 max-sm:pr-6 max-sm:text-xs" value={region} onChange={(event) => setRegion(event.target.value)}><option value="">All regions</option>{availableRegions.map((region) => <option key={region}>{region}</option>)}</select></label>
      <span className="whitespace-nowrap text-sm text-stone-600 max-sm:text-xs">{filtered.length} {model.trips ? "trips" : "photos"}</span>
    </div>
    {!model.trips && activeImage && <div className="pb-2 text-sm font-medium">
      <div className="relative -mx-6 border-y border-[#233d35]/15 bg-[#e5ebe2] px-3 py-2 pr-16 sm:px-6 text-xl font-semibold sm:py-3 sm:pr-16 sm:text-2xl">
        <DateView model={{ renderAs: "div", dateFormat: "dateLong" }} value={activeImage._crdate} />
        <button type="button" aria-label="Back to top" title="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: "instant" })} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded hover:bg-[#233d35]/10">
          <Icon icon="arrow-up" className="h-5 w-5" />
        </button>
      </div>
      <div className="pt-3 text-lg font-medium max-sm:-mx-3">{photoHeading(activeImage)}</div>
    </div>}
    </div>
    {error && <p role="alert">{error}</p>}
    {!availableRegions.length && <p className="text-sm text-stone-600">Region information is not available for these photos yet.</p>}
    {model.trips && content}
    {!model.trips && <div ref={timelineRef} className="my-2 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {filtered.slice(0, count).map((image, index) => {
        const heading = photoHeading(image);
        const dayChanged = !index || new Date(image._crdate).toDateString() !== new Date(filtered[index - 1]._crdate).toDateString();
        const changed = dayChanged || heading !== photoHeading(filtered[index - 1]);
        return <Fragment key={image._id}>
          {index > 0 && dayChanged && <DateView model={{ renderAs: "h2", dateFormat: "dateLong", className: "col-span-full mt-4 border-y border-[#233d35]/15 bg-[#e5ebe2] -mx-6 px-3 py-2 text-xl sm:px-6 sm:py-3 sm:text-2xl font-semibold" }} value={image._crdate} />}
          {index > 0 && changed && <h3 className={`col-span-full text-lg font-medium max-sm:-mx-3 ${dayChanged ? "" : "border-t border-stone-300 pt-4"}`}>{heading}</h3>}
          <PhotographTimelineImage image={image} openImage={openImage} imageTags={imageTags} tag={tags[image._id] || image.tag || []} toggleTag={toggleTag} />
        </Fragment>;
      })}
    </div>}
    {image && createPortal(<dialog ref={dialogRef} aria-label="Photo viewer" onCancel={() => openImage(undefined)} onKeyDown={(event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const index = filtered.findIndex((item) => item._id === image._id) + (event.key === "ArrowRight" ? 1 : -1);
      if (filtered[index]) openImage(filtered[index]);
    }} className="fixed inset-0 m-0 h-screen max-h-none w-screen max-w-none border-0 bg-black p-0 text-white">
      <img src={`/${image.file.type}/2400x2400/${image.file.id}.${image.file.ext}`} alt={image.alt || image.title || image.file.name} className="h-full w-full object-contain object-top" />
      <button type="button" aria-label="Close photo viewer" className="absolute right-6 top-6 rounded bg-black/70 px-4 py-2 text-white" onClick={() => openImage(undefined)}>Close ×</button>
      <p className="absolute bottom-6 left-6 max-w-[calc(100%-3rem)] rounded bg-black/70 px-4 py-3 text-sm">{image.inferredLocation || image.place || [photoRegion(image).country, photoRegion(image).region].filter(Boolean).join(" · ") || "Location unknown"}</p>
    </dialog>, document.body)}
    {!model.trips && (filtered.length ? count < filtered.length && <div ref={sentinelRef} className="py-8 text-center text-sm text-stone-600">Loading more photos…</div> : <p className="py-8">No photos match this selection.</p>)}
  </Wrapper>;
};

export default PhotographRegions;
